import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Scan, Check, X, AlertCircle, Users, Plus, TrendingUp } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Period = "7d" | "30d" | "all";

function confidenceColor(score: number) {
  if (score >= 0.85) return "bg-green-500";
  if (score >= 0.60) return "bg-yellow-500";
  return "bg-red-500";
}

export default function ScanAnalyticsPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>("7d");
  const [techFilter, setTechFilter] = useState("all");
  const [confFilter, setConfFilter] = useState("all");
  const [noMatchFilter, setNoMatchFilter] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideUserId, setOverrideUserId] = useState("");
  const [overrideLimit, setOverrideLimit] = useState("20");
  const [overrideDuration, setOverrideDuration] = useState("1_day");
  const [overrideReason, setOverrideReason] = useState("");

  const { data: analytics, isLoading } = useQuery<any>({
    queryKey: ["/api/admin/scan-analytics", period],
    queryFn: () => authFetch(`/api/admin/scan-analytics?period=${period}`).then(r => r.json()),
  });

  const { data: quotas } = useQuery<any[]>({
    queryKey: ["/api/admin/scan-quota-overrides"],
    queryFn: () => authFetch("/api/admin/scan-quota-overrides").then(r => r.json()),
  });

  const { data: profiles } = useQuery<any[]>({
    queryKey: ["/api/profiles"],
    queryFn: () => authFetch("/api/profiles").then(r => r.json()),
  });

  const createOverride = useMutation({
    mutationFn: (data: any) => authFetch("/api/admin/scan-quota-overrides", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data),
    }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/scan-quota-overrides"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/scan-analytics"] });
      setOverrideOpen(false);
      toast({ title: "Override créé" });
    },
    onError: () => toast({ variant: "destructive", title: "Erreur", description: "Impossible de créer l'override" }),
  });

  const handleCreateOverride = () => {
    if (!overrideUserId) { toast({ variant: "destructive", title: "Sélectionne un technicien" }); return; }
    const today = new Date().toISOString().split("T")[0];
    let validUntil: string | null = null;
    if (overrideDuration === "1_day") validUntil = today;
    else if (overrideDuration === "1_week") {
      const d = new Date(); d.setDate(d.getDate() + 6);
      validUntil = d.toISOString().split("T")[0];
    }
    createOverride.mutate({ user_id: overrideUserId, daily_limit: parseInt(overrideLimit), valid_from: today, valid_until: validUntil, reason: overrideReason });
  };

  const scans: any[] = analytics?.scans || [];
  const filtered = scans.filter(s => {
    if (techFilter !== "all" && s.user_id !== techFilter) return false;
    if (noMatchFilter && !s.user_marked_no_match) return false;
    if (confFilter === "high" && (s.top_confidence || 0) < 0.85) return false;
    if (confFilter === "low" && (s.top_confidence || 0) >= 0.6) return false;
    return true;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Scan className="w-6 h-6" /> Scanner produit — Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">Pilotage de la qualité et des quotas</p>
        </div>
        <div className="flex gap-2">
          {(["7d", "30d", "all"] as Period[]).map(p => (
            <Button key={p} size="sm" variant={period === p ? "default" : "outline"} onClick={() => setPeriod(p)}>
              {p === "7d" ? "7 jours" : p === "30d" ? "30 jours" : "Tout"}
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: "Scans totaux", value: analytics?.total_scans ?? "—", icon: <Scan className="w-4 h-4" /> },
          { label: "Top-1 validé", value: analytics?.top1_rate != null ? `${Math.round(analytics.top1_rate * 100)}%` : "—", icon: <Check className="w-4 h-4 text-green-500" /> },
          { label: "Top-3 validé", value: analytics?.top3_rate != null ? `${Math.round(analytics.top3_rate * 100)}%` : "—", icon: <TrendingUp className="w-4 h-4 text-blue-500" /> },
          { label: "Aucun match", value: analytics?.no_match_rate != null ? `${Math.round(analytics.no_match_rate * 100)}%` : "—", icon: <X className="w-4 h-4 text-orange-500" /> },
          { label: "Latence moy.", value: analytics?.avg_latency_ms ? `${(analytics.avg_latency_ms / 1000).toFixed(1)}s` : "—", icon: <AlertCircle className="w-4 h-4" /> },
          { label: "Coût estimé", value: analytics?.estimated_cost_eur ? `${analytics.estimated_cost_eur.toFixed(3)} €` : "—", icon: <TrendingUp className="w-4 h-4" /> },
        ].map(kpi => (
          <div key={kpi.label} className="border rounded-xl p-4 bg-card">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">{kpi.icon}<span className="text-xs">{kpi.label}</span></div>
            <p className="text-xl font-bold">{isLoading ? <Skeleton className="h-6 w-12" /> : kpi.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="scans">
        <TabsList>
          <TabsTrigger value="scans">Scans récents</TabsTrigger>
          <TabsTrigger value="problematic">Produits problématiques</TabsTrigger>
          <TabsTrigger value="quotas">Quotas</TabsTrigger>
        </TabsList>

        {/* ── Tab: Scans ── */}
        <TabsContent value="scans" className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Select value={techFilter} onValueChange={setTechFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Technicien" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les techs</SelectItem>
                {(profiles || []).map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={confFilter} onValueChange={setConfFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Confiance" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="high">Haute (≥85%)</SelectItem>
                <SelectItem value="low">Basse (&lt;60%)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={noMatchFilter ? "default" : "outline"} size="sm" onClick={() => setNoMatchFilter(v => !v)}>
              Aucun match seulement
            </Button>
          </div>

          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  {["Date", "Technicien", "Photo", "Filtres", "Top suggestion", "Choix", "Action"].map(h => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-t"><td colSpan={7} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td></tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Aucun scan pour cette période</td></tr>
                ) : filtered.map((s: any) => (
                  <tr key={s.id} className="border-t hover:bg-muted/30 cursor-pointer">
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(s.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-3 py-2 text-xs">{s.user_name || "—"}</td>
                    <td className="px-3 py-2">
                      {s.photo_url && <img src={s.photo_url} alt="" className="w-10 h-10 object-cover rounded border" />}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {s.filters_applied?.type && <Badge variant="outline" className="text-[10px] mr-1">{s.filters_applied.type}</Badge>}
                      {(s.filters_applied?.compatible_robots || []).map((r: string) => (
                        <Badge key={r} variant="outline" className="text-[10px] mr-1">{r}</Badge>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {s.top_suggestion ? (
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${confidenceColor(s.top_confidence || 0)}`} />
                          <span className="truncate max-w-[120px]">{s.top_suggestion}</span>
                          <span className="text-muted-foreground">{Math.round((s.top_confidence || 0) * 100)}%</span>
                        </div>
                      ) : s.user_marked_no_match ? (
                        <span className="text-orange-500">Aucun match</span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">{s.selected_product_name || (s.user_marked_no_match ? <span className="text-orange-400">Aucun</span> : "—")}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{s.action_taken || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ── Tab: Problematic products ── */}
        <TabsContent value="problematic" className="space-y-4">
          <p className="text-sm text-muted-foreground">Produits souvent manqués ou confondus</p>
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  {["Produit", "SKU", "Fois suggéré", "Fois raté", "Taux d'échec", "Action"].map(h => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(analytics?.problematic_products || []).length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Aucun produit problématique détecté</td></tr>
                ) : (analytics?.problematic_products || []).map((p: any) => (
                  <tr key={p.product_id} className="border-t">
                    <td className="px-3 py-2 text-sm font-medium">{p.product_name}</td>
                    <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{p.sku}</td>
                    <td className="px-3 py-2 text-center">{p.suggested_count}</td>
                    <td className="px-3 py-2 text-center text-orange-500">{p.missed_count}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-orange-500 rounded-full" style={{ width: `${p.miss_rate * 100}%` }} />
                        </div>
                        <span className="text-xs">{Math.round(p.miss_rate * 100)}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="outline" className="text-xs h-7 gap-1"
                        onClick={() => window.open(`/products?open=${p.product_id}&edit_visual=1`, "_blank")}>
                        Améliorer description visuelle
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ── Tab: Quotas ── */}
        <TabsContent value="quotas" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Quotas par technicien · Limite par défaut : {analytics?.default_limit ?? 10}/jour</p>
            <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5"><Plus className="w-4 h-4" /> Accorder un override</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Override de quota</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-1.5">
                    <Label>Technicien</Label>
                    <Select value={overrideUserId} onValueChange={setOverrideUserId}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                      <SelectContent>
                        {(profiles || []).map((p: any) => (
                          <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Limite journalière</Label>
                      <Input type="number" value={overrideLimit} onChange={e => setOverrideLimit(e.target.value)} min={1} max={999} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Durée</Label>
                      <Select value={overrideDuration} onValueChange={setOverrideDuration}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1_day">1 jour</SelectItem>
                          <SelectItem value="1_week">1 semaine</SelectItem>
                          <SelectItem value="permanent">Permanent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Raison</Label>
                    <Input value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Ex: Journée atelier intensive" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setOverrideOpen(false)}>Annuler</Button>
                    <Button onClick={handleCreateOverride} disabled={createOverride.isPending}>
                      {createOverride.isPending ? "Enregistrement..." : "Créer l'override"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Quota per tech table */}
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  {["Technicien", "Scans aujourd'hui", "Limite effective", "Override actif", ""].map(h => (
                    <th key={h} className="text-left px-3 py-2 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(analytics?.quota_by_user || []).map((u: any) => {
                  const pct = u.used / u.effective_limit;
                  return (
                    <tr key={u.user_id} className="border-t">
                      <td className="px-3 py-2 flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{u.user_name}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${pct >= 1 ? "bg-red-500" : pct >= 0.7 ? "bg-orange-500" : "bg-green-500"}`}
                              style={{ width: `${Math.min(pct, 1) * 100}%` }} />
                          </div>
                          <span className="text-xs">{u.used}/{u.effective_limit}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">{u.effective_limit}</td>
                      <td className="px-3 py-2">
                        {u.override_active ? <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-xs">Actif</Badge> : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <Button size="sm" variant="ghost" className="h-7 text-xs"
                          onClick={() => { setOverrideUserId(u.user_id); setOverrideOpen(true); }}>
                          Override
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Active overrides */}
          {(quotas || []).length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Overrides actifs</p>
              <div className="space-y-2">
                {(quotas || []).map((o: any) => (
                  <div key={o.id} className="border rounded-lg p-3 flex items-center justify-between bg-muted/20">
                    <div>
                      <p className="text-sm font-medium">{o.user_name}</p>
                      <p className="text-xs text-muted-foreground">{o.daily_limit} scans/jour · {o.valid_until ? `jusqu'au ${new Date(o.valid_until).toLocaleDateString("fr-FR")}` : "Permanent"}</p>
                      {o.reason && <p className="text-xs text-muted-foreground italic">{o.reason}</p>}
                    </div>
                    <Badge variant="outline" className="text-xs">{o.created_by_name}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
